import {useTranslation} from 'react-i18next';

import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import {Box, SxProps, Typography} from '@mui/material';

import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {EmpionPaper} from '../../../../../components/EmpionPaper/EmpionPaper';
import {greenColors, orangeColors} from '../../../../../layouts/theme/designSystem';
import {MatchStatus} from '../../../../../types/MatchStatus/MatchStatus';

interface Props {
  onClick: (isInterested?: boolean) => void;
  status: MatchStatus;
}

const ACTION_BUTTON_SX: SxProps = {
  fontSize: '16px',
  py: 1.25,
  borderRadius: '8px',
  '& .MuiButton-startIcon > *:nth-of-type(1)': {fontSize: '20px'},
};

const SECONDARY_BUTTON_SX: SxProps = {
  ...ACTION_BUTTON_SX,
  backgroundColor: 'white',
  borderColor: greenColors[700],
  color: greenColors[700],
  '&:hover': {
    backgroundColor: orangeColors[5],
    borderColor: greenColors[700],
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: 'white',
    borderColor: orangeColors[3],
    color: greenColors[100],
    opacity: 0.6,
  },
};

export const InterestCard = ({onClick, status}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'applicantDashboard.matchDetailsView.showInterestCard'});

  const isRejected = status === MatchStatus.Rejected;
  const canExpressInterest = status === MatchStatus.New;

  const paperStyles: SxProps = {
    minHeight: '364px',
    height: '100%',
    borderRadius: '16px',
    backgroundImage: 'none',
    boxShadow: 'none',
    background: `radial-gradient(circle at top, ${orangeColors[5]} 0%, ${orangeColors[3]} 60%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 3,
    p: 4,
    textAlign: 'center',
  };

  return (
    <EmpionPaper data-testid='interest-card' sx={paperStyles}>
      <Typography variant='h4' sx={{color: greenColors[700]}}>
        {t('title')}
      </Typography>
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 280}}>
        <EmpionButton
          fullWidth
          variant='primary'
          onClick={() => onClick(true)}
          disabled={!canExpressInterest}
          startIcon={<ThumbUpOutlinedIcon />}
          sx={ACTION_BUTTON_SX}
        >
          {t('interested')}
        </EmpionButton>
        <EmpionButton
          fullWidth
          variant='secondary'
          onClick={() => onClick(false)}
          disabled={isRejected}
          startIcon={<ThumbDownOutlinedIcon />}
          sx={SECONDARY_BUTTON_SX}
        >
          {t('notInterested')}
        </EmpionButton>
      </Box>
    </EmpionPaper>
  );
};
